import { __rest } from "tslib";
import { useMemo, useEffect, useRef } from 'react';
import { useAppSelector, userAppSelectorShallowEqual } from '@web-common/state/createStore';
import { api, apis, } from 'api';
import { systemApi } from '@web-common/utils/contact_util';
import { doGetContactData, doMergeContactDataWithServerData, doGetEmailMarkedData, doGetConactDataFromServer } from '@web-common/state/selector/contact';
import lodashGet from 'lodash/get';
import { createAccountEmailKey, getMainAccount } from '@web-common/components/util/contact';
const contactApi = api.requireLogicalApi(apis.contactApiImpl);
const mailPlusCustomerApi = api.requireLogicalApi(apis.mailPlusCustomerApiImpl);
export const getContactEmailRoleByContactModel = (model) => {
    var _a;
    if (model) {
        const edmRole = (_a = model.customerOrgModel) === null || _a === void 0 ? void 0 : _a.role;
        if (edmRole) {
            return edmRole;
        }
        if (model.contact) {
            return model === null || model === void 0 ? void 0 : model.contact.type;
        }
    }
    return 'external';
};
export const useContactModelNames = (params) => {
    const { emails, _account, enableQuery = false } = params;
    const needMatchEmail = useMemo(() => {
        return emails.slice(0, 5);
    }, [emails.length]);
    const names = useAppSelector(state => {
        if (!enableQuery) {
            return {};
        }
        return needMatchEmail.reduce((total, subEmail) => {
            var _a, _b;
            if (!subEmail) {
                return total;
            }
            const accountEmailKey = createAccountEmailKey(subEmail, _account);
            const contactId = state.contactReducer.emailIdMap[accountEmailKey];
            const name = (_b = (_a = state.contactReducer.contactMap[contactId]) === null || _a === void 0 ? void 0 : _a.contact) === null || _b === void 0 ? void 0 : _b.contactName;
            if (name) {
                total[subEmail] = name;
            }
            return total;
        }, {});
    }, userAppSelectorShallowEqual);
    useEffect(() => {
        if (!enableQuery) {
            return;
        }
        const matchedEmails = Object.keys(names);
        needMatchEmail.filter(item => {
            if (!matchedEmails.includes(item)) {
                doGetContactData({
                    email: item,
                    _account,
                });
            }
        });
    }, [Object.keys(names).length]);
    return names;
};
export const useContactModelList = (emails, _account) => {
    const res = useAppSelector(state => {
        const contactIds = [];
        const modelList = [];
        emails.forEach(email => {
            const accountEmailKey = createAccountEmailKey(email, _account);
            const contactId = state.contactReducer.emailIdMap[accountEmailKey];
            if (contactId) {
                contactIds.push(contactId);
                const model = state.contactReducer.contactMap[contactId];
                if (model) {
                    modelList.push(model);
                }
            }
        });
        return {
            contactIds,
            modelList,
        };
    }, (left, right) => {
        const leftStr = left.contactIds.join('_');
        const rightStr = right.contactIds.join('-');
        return leftStr === rightStr;
    });
    return res.modelList;
};
const useFetchContactModelWhenMissing = (params) => {
    var _a;
    const _b = params || {}, { model } = _b, data = __rest(_b, ["model"]);
    const modelId = (_a = model === null || model === void 0 ? void 0 : model.contact) === null || _a === void 0 ? void 0 : _a.id;
    useEffect(() => {
        if (!modelId) {
            doGetContactData(Object.assign(Object.assign({}, data), { useIdFirst: !!data.contactId && !data.email }));
        }
    }, [modelId, data.contactId, data.email]);
};
export const useContactModel = (params) => {
    var _a;
    const user = systemApi.getCurrentUser(params._account || '');
    const email = (_a = params.email) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase();
    const isSelf = email === (user === null || user === void 0 ? void 0 : user.id);
    const contactId = params.contactId;
    const _account = params._account || (user === null || user === void 0 ? void 0 : user.id);
    const useIdFirst = params.useIdFirst || false;
    const contactModel = useAppSelector(state => {
        var _a, _b, _c;
        let model;
        const userModel = user === null || user === void 0 ? void 0 : user.contact;
        const useCompositeQuery = params.useCompositeQuery || false;
        if (lodashGet(contactId, 'length', 0) && useCompositeQuery) {
            const matchedContact = state.contactReducer.contactMap[contactId];
            if (matchedContact && ((_a = matchedContact.contact) === null || _a === void 0 ? void 0 : _a.type) === 'personal') {
                return matchedContact;
            }
        }
        if (useIdFirst && contactId) {
            model = state.contactReducer.contactMap[contactId];
        }
        else if (isSelf && userModel) {
            if ((_b = userModel === null || userModel === void 0 ? void 0 : userModel.contact) === null || _b === void 0 ? void 0 : _b.id) {
                model = state.contactReducer.contactMap[(_c = userModel === null || userModel === void 0 ? void 0 : userModel.contact) === null || _c === void 0 ? void 0 : _c.id];
            }
            if (!model) {
                model = Object.assign(Object.assign({}, userModel), { isFull: true });
            }
        }
        else if (email) {
            const accountEmailKey = createAccountEmailKey(email, _account);
            const contactId = state.contactReducer.emailIdMap[accountEmailKey];
            model = state.contactReducer.contactMap[contactId];
        }
        else if (contactId) {
            model = state.contactReducer.contactMap[contactId];
        }
        return model;
    }, userAppSelectorShallowEqual);
    useFetchContactModelWhenMissing(Object.assign(Object.assign({}, params), { email, model: contactModel }));
    return contactModel;
};
export const useContactEmailRole = (params) => {
    var _a, _b;
    const { email, _account = getMainAccount(), name } = params;
    const model = useContactModel(Object.assign({}, params));
    useEffect(() => {
        var _a;
        if ((process.env.BUILD_ISEDM || ((_a = systemApi.doGetProductInfo()) === null || _a === void 0 ? void 0 : _a.navigateVersion)) && params.email) {
            mailPlusCustomerApi.doUpdateDisplayEmailLabelMap({
                email,
                name,
                _account,
                action: 'add',
            });
        }
    }, [params.email, params._account]);
    const role = useMemo(() => {
        return getContactEmailRoleByContactModel(model);
    }, [(_a = model === null || model === void 0 ? void 0 : model.customerOrgModel) === null || _a === void 0 ? void 0 : _a.role, (_b = model === null || model === void 0 ? void 0 : model.contact) === null || _b === void 0 ? void 0 : _b.type]);
    return role;
};
export const useCustomerModel = (params) => {
    const { customerId, email, emailRole } = params;
    const model = useAppSelector(state => {
        if (customerId) {
            return state.contactReducer.customerMap[customerId];
        }
        return undefined;
    }, userAppSelectorShallowEqual);
    useEffect(() => {
        if (!model && customerId && email && emailRole !== 'colleagueCustomerNoAuth' && emailRole !== 'colleagueClueNoAuth') {
        }
    }, [customerId, model === null || model === void 0 ? void 0 : model.id, emailRole]);
    return model;
};
export const useUpdateContactModel = (params) => {
    var _a;
    const _b = params || {}, { model } = _b, data = __rest(_b, ["model"]);
    const { contactId, email } = data;
    const modelId = (_a = model === null || model === void 0 ? void 0 : model.contact) === null || _a === void 0 ? void 0 : _a.id;
    const isFull = model === null || model === void 0 ? void 0 : model.isFull;
    useEffect(() => {
        if (!modelId) {
            doGetContactData(Object.assign(Object.assign({}, data), { useIdFirst: !!contactId && !email }));
        }
    }, [modelId, contactId, email]);
    useEffect(() => {
        const contactType = lodashGet(model, 'contact.type', '');
        if (contactType !== 'enterprise' || (params.needCompleteContact || false) !== true) {
            return;
        }
        doMergeContactDataWithServerData({
            contactModel: model,
            requiredField: ['job'],
            _account: params._account,
        });
    }, [params.contactId, lodashGet(model, 'contact.type', ''), params === null || params === void 0 ? void 0 : params._account, params === null || params === void 0 ? void 0 : params.needCompleteContact]);
    useEffect(() => { });
    useEffect(() => {
        var _a;
        const contactType = lodashGet(model, 'contact.type', '');
        const externalEmail = lodashGet(params, 'email', '');
        const myDomain = ((_a = systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.domain) || '';
        if (!(params === null || params === void 0 ? void 0 : params.needCompleteContact) || contactType === 'enterprise' || contactType === 'personal' || !externalEmail || externalEmail.indexOf(myDomain) == -1) {
            return;
        }
        const tid = setTimeout(() => {
            doGetConactDataFromServer(params.contactId, params.email, params._account);
        }, 200);
        return () => {
            tid && clearTimeout(tid);
        };
    }, [params.email, lodashGet(model, 'contact.type', ''), params === null || params === void 0 ? void 0 : params.needCompleteContact]);
    useEffect(() => {
        const contactType = lodashGet(model, 'contact.type', '');
        const hasYunxin = model === null || model === void 0 ? void 0 : model.contactInfo.find(item => {
            return item.contactItemType === 'yunxin';
        });
        const isMainAccount = systemApi.getIsMainAccount(params._account);
        if (contactType !== 'enterprise' || hasYunxin || !isMainAccount || !isFull) {
            return;
        }
        doGetConactDataFromServer(params.contactId, params.email, params._account);
    }, [isFull, params.email, lodashGet(model, 'contactInfo.length', 0)]);
};
export const useContactMarked = (props) => {
    const { email, orgId, useId, contactId, _account } = props;
    const contactMarked = useAppSelector(state => {
        if (orgId) {
            return state.contactReducer.orgMarkedMap[orgId];
        }
        if (email) {
            const contactIdList = state.contactReducer.emailMarkedMap[email];
            if (useId && contactId && contactIdList) {
                return contactIdList.includes(contactId);
            }
            if (!contactIdList) {
                return undefined;
            }
            return contactIdList.length > 0;
        }
        return undefined;
    }, userAppSelectorShallowEqual);
    useMemo(() => {
        if (contactMarked === undefined) {
            doGetEmailMarkedData({ email, orgId, _account });
        }
    }, [email]);
    return !!contactMarked;
};
export const useContactPersonalOrg = (personalIdList) => {
    return useAppSelector(state => {
        if (!(personalIdList === null || personalIdList === void 0 ? void 0 : personalIdList.length)) {
            return {
                personalOrgNameList: [],
                hasDataOrgIds: [],
            };
        }
        const personalOrgNameList = [];
        const hasDataOrgIds = [];
        personalIdList.forEach(id => {
            const orgInfo = state.contactReducer.orgMap[id];
            if (orgInfo) {
                hasDataOrgIds.push(id);
                personalOrgNameList.push(orgInfo.orgName);
            }
        });
        return { personalOrgNameList, hasDataOrgIds };
    }, (prev, cur) => {
        const { personalOrgNameList: left, hasDataOrgIds: orgLeft } = prev;
        const { personalOrgNameList: right, hasDataOrgIds: orgRight } = cur;
        if ((left === null || left === void 0 ? void 0 : left.length) === (right === null || right === void 0 ? void 0 : right.length)) {
            const leftSet = new Set(left);
            return left.length > 0 && !right.some(item => !leftSet.has(item));
        }
        if ((orgLeft === null || orgLeft === void 0 ? void 0 : orgLeft.length) === (orgRight === null || orgRight === void 0 ? void 0 : orgRight.length)) {
            const leftOrgSet = new Set(orgLeft);
            return orgLeft.length > 0 && !orgRight.some(item => !leftOrgSet.has(item));
        }
        return false;
    });
};
export const useContactPersonalOrgList = (personalIdList) => {
    if (!personalIdList) {
        return [];
    }
    return useAppSelector(state => {
        const personalOrgNameList = [];
        personalIdList.forEach(id => {
            const orgInfo = state.contactReducer.orgMap[id];
            if (orgInfo) {
                personalOrgNameList.push(orgInfo);
            }
        });
        return personalOrgNameList;
    }, userAppSelectorShallowEqual);
};
export const useContactAvatar = (props) => {
    const { model, email: _email, name, emailTxt, avatar: defaultAvatar = '', logcAvatar = '', color: defaultColor = '', showAvatarFirst = true } = props;
    let email = _email === null || _email === void 0 ? void 0 : _email.toLocaleLowerCase();
    const res = {
        avatar: defaultAvatar,
        avatarTxt: '',
        avatarPendant: '',
        color: defaultColor,
    };
    if (model) {
        const { avatar, avatarPendant, contactName, color } = model.contact;
        email = email || contactApi.doGetModelDisplayEmail(model);
        res.avatarTxt = contactName || contactApi.doGetModelDisplayEmail(model);
        res.avatar = showAvatarFirst && avatar ? avatar : '';
        res.avatarPendant = avatarPendant || '';
        res.color = color || contactApi.getColor(email || contactName);
    }
    else {
        res.avatarTxt = name || emailTxt || '';
        if (!defaultColor) {
            res.color = contactApi.getColor(email || emailTxt || '');
        }
    }
    res.avatar = showAvatarFirst ? res.avatar || logcAvatar : '';
    return res;
};
const getCustomerDetailKeys = (list) => {
    let ids = '', names = '', contactIds = '', contactNames = '', managerIds = '';
    list === null || list === void 0 ? void 0 : list.forEach(detail => {
        var _a, _b;
        if (detail) {
            const { curIds, curNames } = (_a = detail.contactList) === null || _a === void 0 ? void 0 : _a.reduce((obj, cur) => {
                let { curIds, curNames } = obj;
                curIds += cur.id;
                curNames += cur.name;
                return { curIds, curNames };
            }, { curIds: '', curNames: '' });
            const mIds = (_b = detail.managerList) === null || _b === void 0 ? void 0 : _b.reduce((str, cur) => {
                str += cur.managerId;
                return str;
            }, '');
            ids += detail.id;
            names += detail.orgName;
            contactIds += curIds;
            contactNames += curNames;
            contactIds += curIds;
            contactNames += curNames;
            managerIds += mIds;
        }
    });
    return {
        ids,
        names,
        contactIds,
        contactNames,
        managerIds,
    };
};
const compareCustomerList = (left, right) => {
    const { ids: leftIds, names: leftNames, contactIds: leftContactIds, contactNames: leftContactNames, managerIds: leftManagerIds } = getCustomerDetailKeys(left);
    const { ids: rightIds, names: rightNames, contactIds: rightContactIds, contactNames: rightContactNames, managerIds: rightManagerIds } = getCustomerDetailKeys(right);
    if (leftIds !== rightIds) {
        return false;
    }
    if (leftNames !== rightNames) {
        return false;
    }
    if (leftContactIds !== rightContactIds) {
        return false;
    }
    if (leftContactNames !== rightContactNames) {
        return false;
    }
    if (leftManagerIds !== rightManagerIds) {
        return false;
    }
    return true;
};
export const useMyCustomerList = (idList) => {
    return useAppSelector(state => {
        const customerMap = state.contactReducer.customerMap;
        const detailList = [];
        idList.forEach(id => {
            const detail = customerMap[id];
            if (detail) {
                detailList.push(detail);
            }
        });
        return detailList;
    }, (left, right) => compareCustomerList(left, right));
};
export const useMyCustomerListCallback2 = (idList, callback) => {
    const detailList = useAppSelector(state => {
        const customerMap = state.contactReducer.customerMap;
        const detailList = [];
        idList.forEach(id => {
            const detail = customerMap[id];
            if (detail) {
                detailList.push(detail);
            }
        });
        return detailList;
    }, userAppSelectorShallowEqual);
    const lastData = useRef([]);
    useEffect(() => {
        const isEqual = compareCustomerList(lastData.current, detailList);
        if (!isEqual) {
            callback(detailList);
            lastData.current = detailList;
        }
    }, [detailList]);
};
export const useMyCustomerListCallback = (idList, callback) => {
    const customerMap = useAppSelector(state => state.contactReducer.customerMap, userAppSelectorShallowEqual);
    const lastData = useRef([]);
    const ids = idList.reduce((str, id) => {
        str += id;
        return str;
    }, '');
    useEffect(() => {
        const detailList = [];
        idList.forEach(id => {
            const detail = customerMap[id];
            if (detail) {
                detailList.push(detail);
            }
        });
        const isEqual = compareCustomerList(lastData.current, detailList);
        if (!isEqual) {
            callback(detailList);
            lastData.current = detailList;
        }
    }, [customerMap, ids]);
};
